import {
  HistoryService,
  HistoryOperations,
  OperationHistoryEntry,
  UserRelevantOperationsSinceLastVisit,
} from '@beeboard/common';
import { defineStore } from 'pinia';
import { useChecklistStore } from './checklistStore';
import { useUserStore } from './userStore';

type HistoryStoreState = {
  operationsHistory: Record<string, HistoryOperations>;
  lastHistoryOperation: Record<string, OperationHistoryEntry | null>;
  catchUpOperations: Record<string, UserRelevantOperationsSinceLastVisit>;
};

interface FilterOptions {
  from?: string;
  to?: string;
  operationId?: string;
  operationType?: string[];
  entityTypes?: string[];
  userId?: string;
  entityId?: string;
}

interface PagingOptions {
  pageNumber?: number;
  pageSize?: number;
}

/**
 * Per-project bookkeeping for `fetchLastHistoryOperation`. Deliberately module
 * scope rather than store state: it is call coordination, nothing renders from it,
 * and it must not be reset by a store reset while a request is still in flight.
 */
type LatestOperationFetch = {
  /** The request currently running, shared with every caller that arrives during it. */
  inFlight: Promise<void> | null;
  /** A caller arrived mid-flight, so run exactly one more fetch when this one settles. */
  refetchQueued: boolean;
  /**
   * Bumped when a fetch starts and whenever the operation is cleared. A response
   * whose token no longer matches is discarded instead of written to state.
   */
  sequence: number;
};

const latestOperationFetches = new Map<string, LatestOperationFetch>();

function latestOperationFetch(projectId: string): LatestOperationFetch {
  let fetchState = latestOperationFetches.get(projectId);

  if (!fetchState) {
    fetchState = { inFlight: null, refetchQueued: false, sequence: 0 };
    latestOperationFetches.set(projectId, fetchState);
  }

  return fetchState;
}

export const useHistoryStore = defineStore('historyStore', {
  state: (): HistoryStoreState => ({
    operationsHistory: {},
    lastHistoryOperation: {},
    catchUpOperations: {},
  }),

  getters: {
    getOperationsHistory: (state) => (projectId: string) => {
      return state.operationsHistory[projectId] || { data: [], total: 0 };
    },

    getLastOperation: (state) => (projectId: string) => {
      return state.lastHistoryOperation[projectId] || null;
    },

    getCatchupSummary: (state) => (projectId: string) => {
      const data = state.catchUpOperations[projectId];
      if (!data) {
        return null;
      }

      return {
        relevantOperations: data.relevant_to_user,
        relevantCount: data.relevant_to_user.length,
        otherOperations: data.other_changes,
        otherCount: data.other_changes.length,
        totalCount: data.relevant_to_user.length + data.other_changes.length,
        lastReadAt: data.last_read_at,
      };
    },

    hasProjectCatchUpOperations: (state) => (projectId: string) => {
      const data = state.catchUpOperations[projectId];
      if (!data) {
        return false;
      }

      // Show ONLY if last_read_at is not null AND there are changes
      return (
        data.last_read_at !== null &&
        (data.relevant_to_user.length > 0 || data.other_changes.length > 0)
      );
    },
  },

  actions: {
    async fetchAllOperationsHistory(
      projectId: string,
      filters: FilterOptions,
      paging: PagingOptions,
    ) {
      const history = await HistoryService.getOperationsHistory(
        projectId,
        filters.operationId,
        filters.from,
        filters.to,
        filters.operationType,
        filters.entityTypes,
        filters.userId,
        filters.entityId,
        paging.pageNumber,
        paging.pageSize,
      );
      this.operationsHistory[projectId] = history;
    },

    /**
     * Refreshes the project's newest operation, which is what enables/disables the
     * undo button.
     *
     * Called after essentially every undoable store action, so it does two things
     * beyond fetching:
     *
     * - **Coalescing.** While a request is in flight, further callers ride on it and
     *   request a single follow-up fetch instead of issuing one request each. A burst
     *   of undoable actions therefore costs 2 requests, not one per action, and a
     *   lone action pays no extra latency.
     * - **Sequencing.** Each run carries a token that `clearLastHistoryOperation`
     *   also invalidates. Without it a response already in flight can land *after* a
     *   clear and resurrect the operation it just cleared, re-enabling undo for an
     *   older operation than the user expects.
     *
     * The returned promise resolves only once state has been written, because
     * callers await it and then read `lastHistoryOperation[projectId]` directly.
     */
    async fetchLastHistoryOperation(projectId: string): Promise<void> {
      const fetchState = latestOperationFetch(projectId);

      if (fetchState.inFlight) {
        // The running response may predate whatever this caller just changed, so
        // ask for one refresh after it rather than racing a second request now.
        fetchState.refetchQueued = true;

        return fetchState.inFlight;
      }

      const run = async (): Promise<void> => {
        const token = ++fetchState.sequence;
        const { data } =
          await HistoryService.getLatestOperationsHistory(projectId);

        if (token !== fetchState.sequence) {
          return;
        }

        this.lastHistoryOperation[projectId] = data;
      };

      const runThenDrainQueue = async (): Promise<void> => {
        try {
          await run();
        } finally {
          fetchState.inFlight = null;
        }

        if (fetchState.refetchQueued) {
          fetchState.refetchQueued = false;
          await this.fetchLastHistoryOperation(projectId);
        }
      };

      fetchState.inFlight = runThenDrainQueue();

      return fetchState.inFlight;
    },

    clearLastHistoryOperation(projectId: string) {
      // Invalidates any response still in flight so it cannot write the operation
      // back after this clear.
      latestOperationFetch(projectId).sequence += 1;
      delete this.lastHistoryOperation[projectId];
    },

    undoOperation(projectId: string, operationId: string) {
      return HistoryService.undoOperation(projectId, operationId).then(
        async () => {
          const operationHistory = this.operationsHistory[projectId];
          const operationIndex = operationHistory?.data.findIndex(
            (operation) => operation.id === operationId,
          );
          if (operationIndex > -1) {
            this.operationsHistory[projectId].data.splice(operationIndex, 1);
          }

          // The backend undo endpoint emits no `project:updated` realtime
          // event (plain transactionContext), so loaded checklist data must
          // be refetched here explicitly. Best-effort: both refresh actions
          // no-op unless the grid/pool is currently loaded.
          try {
            const checklistStore = useChecklistStore();
            await Promise.all([
              checklistStore.refreshActivityChecklist(projectId),
              checklistStore.refreshPoolRows(projectId),
            ]);
          } catch {
            // Checklist refresh is best-effort — never fail the undo flow
          }
        },
      );
    },

    async fetchProjectCatchUpOperations(projectId: string) {
      const data = await HistoryService.getOperationsHistoryCatchup(projectId);
      this.catchUpOperations[projectId] = data;

      const isFirstTimeVisitor = data.last_read_at === null;
      const hasNoChanges =
        data.relevant_to_user.length === 0 && data.other_changes.length === 0;

      // If the user is visiting for the first time or there are no changes,
      if (isFirstTimeVisitor || hasNoChanges) {
        await useUserStore().updateUserProjectActivity(projectId, {
          catch_up_read: true,
        });
      }

      return data;
    },

    clearProjectCatchUpOperations(projectId: string) {
      delete this.catchUpOperations[projectId];
    },
  },
});
